import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { HiOutlineX } from 'react-icons/hi';
import { useAuthStore } from '../../stores';
import { updateUserProfile } from '../../services/userService';
import './EditProfileDialog.css';

interface EditProfileDialogProps {
  isOpen: boolean;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLElement | null>;
}

export default function EditProfileDialog({ isOpen, onClose, anchorRef }: EditProfileDialogProps) {
  const user = useAuthStore((state) => state.user);
  const setUser = useAuthStore((state) => state.setUser);

  const [name, setName] = useState('');
  const [title, setTitle] = useState('');
  const [department, setDepartment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [position, setPosition] = useState<{ left: number; bottom: number } | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && user) {
      setName(user.name || '');
      setTitle((user as any).title || '');
      setDepartment((user as any).department || '');
      setError('');
    }
  }, [isOpen, user]);

  // Position the popover above the trigger element, tracking window resize/scroll.
  useLayoutEffect(() => {
    if (!isOpen || !anchorRef.current) {
      setPosition(null);
      return;
    }

    const updatePosition = () => {
      if (!anchorRef.current) return;
      const rect = anchorRef.current.getBoundingClientRect();
      setPosition({
        left: rect.left,
        bottom: window.innerHeight - rect.top + 10,
      });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen, anchorRef]);

  // Close when clicking outside both the popover and its trigger.
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (popoverRef.current?.contains(target)) return;
      if (anchorRef.current?.contains(target)) return;
      onClose();
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose, anchorRef]);

  if (!isOpen || !position) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!user || !(user as any).id) {
      setError('You must be logged in to edit your profile.');
      return;
    }
    const userId = (user as any).id;

    if (!name.trim()) {
      setError('Name is required.');
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await updateUserProfile({
        id: userId,
        name: name.trim(),
        title: title.trim() || undefined,
        department: department.trim() || undefined,
      });

      if (result.success) {
        setUser({ ...user, name: name.trim(), title: title.trim(), department: department.trim() });
        onClose();
      } else {
        setError(result.error || 'Failed to update profile. Please try again.');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return ReactDOM.createPortal(
    <div
      className="edit-profile-popover"
      ref={popoverRef}
      style={{ left: position.left, bottom: position.bottom }}
    >
      <div className="edit-profile-popover-header">
        <span>Edit Profile</span>
        <button type="button" onClick={onClose} className="edit-profile-close-btn">
          <HiOutlineX />
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="edit-profile-popover-body">
          <div className="edit-profile-form-group">
            <label>Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Full name"
              className="edit-profile-input"
              disabled={isSubmitting}
              autoFocus
            />
          </div>

          <div className="edit-profile-form-group">
            <label>Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Business Development Officer"
              className="edit-profile-input"
              disabled={isSubmitting}
            />
          </div>

          <div className="edit-profile-form-group">
            <label>Department</label>
            <input
              type="text"
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
              placeholder="e.g. Sales"
              className="edit-profile-input"
              disabled={isSubmitting}
            />
          </div>

          {error && <p className="edit-profile-error">{error}</p>}
        </div>

        <div className="edit-profile-popover-footer">
          <button type="button" onClick={onClose} className="edit-profile-btn-cancel">
            Cancel
          </button>
          <button
            type="submit"
            disabled={!name.trim() || isSubmitting}
            className="edit-profile-btn-submit"
          >
            {isSubmitting ? 'Saving...' : 'Save'}
          </button>
        </div>
      </form>
    </div>,
    document.body
  );
}
